import { Check, ChevronDown, LayoutGrid, List } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/utils'
import { COLOR_FAMILIES, type ColorFamily } from '@/lib/colors/classify-family'

interface ColorPopoverToolbarProps {
  colorFamily: ColorFamily
  onColorFamilyChange: (family: ColorFamily) => void
  colorLayout: 'grid' | 'list'
  onColorLayoutChange: (layout: 'grid' | 'list') => void
}

export function ColorPopoverToolbar({
  colorFamily,
  onColorFamilyChange,
  colorLayout,
  onColorLayoutChange,
}: ColorPopoverToolbarProps) {
  return (
    <div className="flex items-center justify-between px-1 pt-1 pb-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 h-8 px-3 rounded-md border border-input bg-background shadow-xs text-xs font-medium text-foreground"
          >
            {colorFamily}
            <ChevronDown className="size-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[160px]">
          {COLOR_FAMILIES.map((family) => (
            <DropdownMenuItem
              key={family}
              onSelect={() => onColorFamilyChange(family)}
              className="flex items-center justify-between gap-2"
            >
              {family}
              {colorFamily === family && <Check className="size-3.5 text-primary shrink-0" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex items-center gap-0.5">
        <button
          type="button"
          aria-label="Grid view"
          aria-pressed={colorLayout === 'grid'}
          onClick={() => onColorLayoutChange('grid')}
          className={cn(
            'flex items-center justify-center size-8 rounded-md transition-colors',
            colorLayout === 'grid' ? 'bg-secondary shadow-xs' : 'bg-transparent hover:bg-muted'
          )}
        >
          <LayoutGrid className="size-4 text-foreground" />
        </button>
        <button
          type="button"
          aria-label="List view"
          aria-pressed={colorLayout === 'list'}
          onClick={() => onColorLayoutChange('list')}
          className={cn(
            'flex items-center justify-center size-8 rounded-md transition-colors',
            colorLayout === 'list' ? 'bg-secondary shadow-xs' : 'bg-transparent hover:bg-muted'
          )}
        >
          <List className="size-4 text-foreground" />
        </button>
      </div>
    </div>
  )
}
